import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Home, Briefcase, Store, ShieldCheck } from "lucide-react";
import { toast } from "react-hot-toast";
import { adminService } from "@/services/adminService";
import { sellerAccountService } from "@/services/sellerAccountService";
import { hostService } from "@/services/hostService";
import { staffAccessService } from "@/services/staffAccessService";
import {
  Permission,
  type HostCreate,
  type SalesAgentCreatePayload,
  type SellerAccountCreate,
} from "@/types";
import { toastApiError } from "@/lib/errorMessages";
import { useImpersonation } from "@/hooks/useImpersonation";
import { useAuthStore } from "@/store/authStore";
import {
  AdminUserListPage,
  CreateAdminUserModal,
  CreateStaffUserModal,
  adminUserRowToListItem,
  useAdminUserCreateModalLabels,
} from "@/components/admin/users";
import type {
  AdminUserListItem,
  AdminUserListLabels,
  CreateStaffUserModalLabels,
} from "@/components/admin/users";
import type { AdminRegionFilterValue } from "@/components/admin/AdminRegionFilter";
import { Modal, OptionCard } from "@/components/ui";
import { cn } from "@/lib/utils";

type CreateRole = "seller" | "host" | "sales-agent" | "staff";
type ModalStep = "closed" | "pick-role" | CreateRole;

function userDetailPath(
  item: AdminUserListItem,
  canEditStaffAccess: boolean,
): string | null {
  switch (item.role) {
    case "seller":
      return `/sellers/${item.userId}`;
    case "host":
      return `/hosts/${item.userId}`;
    case "sales-agent":
      return `/admin/sales-agents/${item.userId}`;
    case "admin":
      return canEditStaffAccess ? `/admin/staff/${item.userId}` : null;
  }
}

function useUnifiedAdminUserListLabels(): AdminUserListLabels {
  const { t } = useTranslation("adminUsers");

  return {
    title: t("unified.title"),
    subtitle: t("unified.subtitle"),
    addButton: t("unified.addButton"),
    searchPlaceholder: t("unified.searchPlaceholder"),
    loadError: t("unified.loadError"),
    retry: t("list.retry"),
    colName: t("list.colName"),
    colContact: t("list.colContact"),
    colBalance: t("list.colBalance"),
    colRelevant: t("unified.colCount"),
    colActions: t("list.colActions"),
    empty: t("unified.empty"),
    emptySearch: t("list.emptySearch"),
    statusActive: t("list.statusActive"),
    statusInactive: t("list.statusInactive"),
    impersonate: t("impersonate"),
    openRow: (name) => t("list.openRowAria", { name }),
  };
}

function useCreateStaffUserModalLabels(): CreateStaffUserModalLabels {
  const { t } = useTranslation("adminUsers");
  const { t: tCommon } = useTranslation("common");

  return {
    title: t("createStaffModal.title"),
    nameLabel: t("createModal.nameLabel"),
    namePlaceholder: t("createStaffModal.namePlaceholder"),
    emailLabel: t("createModal.emailLabel"),
    emailPlaceholder: t("createStaffModal.emailPlaceholder"),
    phoneLabel: t("createModal.phoneLabel"),
    phonePlaceholder: t("createModal.phonePlaceholder"),
    phoneRequired: t("createModal.phoneRequired"),
    regionLabel: t("createModal.regionLabel"),
    regionPlaceholder: t("createModal.chooseRegion"),
    regionRequired: t("createModal.regionRequired"),
    presetLabel: t("createStaffModal.presetLabel"),
    choosePreset: t("createStaffModal.choosePreset"),
    presetRequired: t("createStaffModal.presetRequired"),
    inviteEmailHint: t("createModal.inviteEmailHint"),
    cancel: tCommon("cancel"),
    create: t("createStaffModal.create"),
  };
}

const CREATE_ROLE_OPTIONS: {
  role: CreateRole;
  icon: typeof Store;
  iconClassName: string;
  labelKey: "seller" | "host" | "salesAgent" | "staff";
  requiredPermission?: Permission;
}[] = [
  {
    role: "seller",
    icon: Store,
    iconClassName: "bg-primary-100 text-primary-600",
    labelKey: "seller",
  },
  {
    role: "host",
    icon: Home,
    iconClassName: "bg-success-100 text-success-600",
    labelKey: "host",
  },
  {
    role: "sales-agent",
    icon: Briefcase,
    iconClassName: "bg-violet-100 text-violet-600",
    labelKey: "salesAgent",
  },
  {
    role: "staff",
    icon: ShieldCheck,
    iconClassName: "bg-surface-100 text-surface-600",
    labelKey: "staff",
    requiredPermission: Permission.STAFF_CREATE,
  },
];

export default function AdminUsersPage() {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("adminUsers");
  const { t: tSellers } = useTranslation("sellers");
  const { t: tHosts } = useTranslation("hosts");
  const { t: tSalesAgent } = useTranslation("salesAgent");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const listLabels = useUnifiedAdminUserListLabels();
  const staffCreateLabels = useCreateStaffUserModalLabels();
  const { startImpersonation, isStarting } = useImpersonation();
  const canImpersonate = useAuthStore((state) =>
    state.hasPermission(Permission.USERS_IMPERSONATE),
  );
  const canCreateStaff = useAuthStore((state) =>
    state.hasPermission(Permission.STAFF_CREATE),
  );
  const canEditStaffAccess = useAuthStore((state) =>
    state.hasPermission(Permission.STAFF_PERMISSIONS_UPDATE),
  );
  const canImpersonateStaff = canEditStaffAccess;
  const [modalStep, setModalStep] = useState<ModalStep>("closed");
  const [regionFilter, setRegionFilter] = useState<AdminRegionFilterValue>(null);

  const sellerCreateLabels = useAdminUserCreateModalLabels({
    title: tSellers("createModal.title"),
    namePlaceholder: tSellers("createModal.namePlaceholder"),
    emailPlaceholder: tSellers("createModal.emailPlaceholder"),
    create: tSellers("createModal.create"),
  });
  const hostCreateLabels = useAdminUserCreateModalLabels({
    title: tHosts("createModal.title"),
    namePlaceholder: tHosts("createModal.namePlaceholder"),
    emailPlaceholder: tHosts("createModal.emailPlaceholder"),
    create: tHosts("createModal.create"),
  });
  const salesAgentCreateLabels = useAdminUserCreateModalLabels({
    title: tSalesAgent("create.title"),
    namePlaceholder: tSalesAgent("create.namePlaceholder"),
    emailPlaceholder: tSalesAgent("create.emailPlaceholder"),
    create: tSalesAgent("create.submit"),
  });

  const {
    data: users = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin", "users", regionFilter],
    queryFn: () =>
      adminService.getUsers({
        region_id: regionFilter ?? undefined,
      }),
  });

  const createSellerMutation = useMutation({
    mutationFn: (data: SellerAccountCreate) => sellerAccountService.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "sellers"] });
      void queryClient.invalidateQueries({ queryKey: ["sellers"] });
      toast.success(tSellers("createSuccess"));
      setModalStep("closed");
    },
    onError: (err) => {
      toastApiError(err, "createSeller");
    },
  });

  const createHostMutation = useMutation({
    mutationFn: (data: HostCreate) => hostService.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "hosts"] });
      void queryClient.invalidateQueries({ queryKey: ["hosts"] });
      toast.success(tHosts("createSuccess"));
      setModalStep("closed");
    },
    onError: (err) => {
      toastApiError(err, "createHost");
    },
  });

  const createSalesAgentMutation = useMutation({
    mutationFn: (payload: SalesAgentCreatePayload) =>
      adminService.createSalesAgent(payload),
    onSuccess: () => {
      toast.success(tSalesAgent("createSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "sales-agents"] });
      setModalStep("closed");
    },
    onError: (e: unknown) => {
      toastApiError(e, "createSalesAgent");
    },
  });

  const createStaffMutation = useMutation({
    mutationFn: staffAccessService.create,
    onSuccess: (created) => {
      toast.success(t("createStaffModal.createSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setModalStep("closed");
      if (canEditStaffAccess) {
        navigate(`/admin/staff/${created.user_id}`);
      }
    },
    onError: (err) => {
      toastApiError(err, "generic", t("createStaffModal.createError"));
    },
  });

  const items = useMemo(() => users.map(adminUserRowToListItem), [users]);
  const roleOptions = CREATE_ROLE_OPTIONS.filter(
    (option) =>
      option.requiredPermission == null ||
      (option.requiredPermission === Permission.STAFF_CREATE && canCreateStaff),
  );

  return (
    <>
      <AdminUserListPage
        items={items}
        isLoading={isLoading}
        error={!!error}
        onRetry={() => void refetch()}
        labels={listLabels}
        detailPath={(item) => userDetailPath(item, canEditStaffAccess)}
        onAddClick={() => setModalStep("pick-role")}
        onImpersonate={
          canImpersonate
            ? (item) => {
                void startImpersonation(item.userId);
              }
            : undefined
        }
        impersonatePending={isStarting}
        canImpersonateStaff={canImpersonateStaff}
        regionFilter={regionFilter}
        onRegionFilterChange={setRegionFilter}
      />

      <Modal
        closeLabel={tCommon("close")}
        isOpen={modalStep === "pick-role"}
        onClose={() => setModalStep("closed")}
        title={t("rolePicker.title")}
        size="lg"
      >
        <div className="grid gap-2.5 sm:grid-cols-2">
          {roleOptions.map((option) => (
            <OptionCard
              key={option.role}
              icon={option.icon}
              tone="primary"
              title={t(`rolePicker.${option.labelKey}`)}
              description={t(`rolePicker.${option.labelKey}Desc`)}
              iconClassName={cn("rounded-lg", option.iconClassName)}
              className="px-4 py-3 transition-all duration-200 hover:-translate-y-px hover:shadow-elevated"
              onClick={() => setModalStep(option.role)}
            />
          ))}
        </div>
      </Modal>

      <CreateAdminUserModal
        isOpen={modalStep === "seller"}
        onClose={() => setModalStep("closed")}
        onSubmit={(data) => {
          createSellerMutation.mutate({
            name: data.name,
            email: data.email,
            region_id: data.region_id,
            contact_phone: data.contact_phone,
          });
        }}
        isLoading={createSellerMutation.isPending}
        labels={sellerCreateLabels}
      />
      <CreateAdminUserModal
        isOpen={modalStep === "host"}
        onClose={() => setModalStep("closed")}
        onSubmit={(data) => {
          createHostMutation.mutate({
            name: data.name,
            email: data.email,
            region_id: data.region_id,
            contact_phone: data.contact_phone,
          });
        }}
        isLoading={createHostMutation.isPending}
        labels={hostCreateLabels}
      />
      <CreateAdminUserModal
        isOpen={modalStep === "sales-agent"}
        onClose={() => setModalStep("closed")}
        onSubmit={(data) => {
          createSalesAgentMutation.mutate({
            name: data.name,
            email: data.email,
            region_id: data.region_id,
            contact_phone: data.contact_phone,
          });
        }}
        isLoading={createSalesAgentMutation.isPending}
        labels={salesAgentCreateLabels}
      />
      <CreateStaffUserModal
        isOpen={modalStep === "staff"}
        onClose={() => setModalStep("closed")}
        onSubmit={(data) => {
          createStaffMutation.mutate(data);
        }}
        isLoading={createStaffMutation.isPending}
        labels={staffCreateLabels}
      />
    </>
  );
}
